'use client';

import { useRef } from 'react';

import { motion } from 'framer-motion';
import { MessageCircle, ScanLine, UserPlus } from 'lucide-react';

import {
  APP_TABS,
  AppTabBar,
  Avatar,
  BrandMark,
  CheckBadge,
  DEMO_BRANDS,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  EASE_OUT_EXPO,
  FakeQr,
  PhoneFrame,
  Reveal,
  SPRING_SNAPPY,
  TapRipple,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * Member app, members club: the digital membership card checks the member in
 * at the entrance, shows the family on the membership, sends a guest pass and
 * receives a WhatsApp renewal reminder with a one-tap renewal link.
 */

const DURATIONS = [1900, 1500, 1800, 1300, 2000, 1700, 3000] as const;

const STEP = {
  scan: 1,
  welcome: 2,
  checkedIn: 3,
  guestSheet: 4,
  guestSent: 5,
  renewal: 6,
} as const;

const COPY = {
  label: {
    en: 'Animated demo of a members club app: a member checks in with a digital membership card, sees their family on the membership, sends a guest pass and gets a WhatsApp renewal reminder.',
    ar: 'عرض متحرك لتطبيق نادي أعضاء: عضو يسجّل دخوله ببطاقة عضويته الرقمية، ويرى أفراد عائلته على العضوية، ويرسل دعوة ضيف، ثم يصله تذكير التجديد عبر واتساب.',
  },
  title: { en: 'Membership', ar: 'العضوية' },
  memberName: { en: 'Omar Al-Rashid', ar: 'عمر الراشد' },
  tier: { en: 'Family membership', ar: 'عضوية عائلية' },
  since: { en: 'Member since 2022', ar: 'عضو منذ 2022' },
  validUntil: { en: 'Valid until 30 Sep', ar: 'سارية حتى 30 سبتمبر' },
  scanHint: { en: 'Hold your card to the entrance scanner', ar: 'قرّب بطاقتك من قارئ المدخل' },
  welcome: { en: 'Welcome back, Omar', ar: 'أهلًا بعودتك يا عمر' },
  checkedIn: { en: 'Checked in · Main entrance · 6:42 PM', ar: 'تم تسجيل الدخول · المدخل الرئيسي · 6:42 م' },
  checkedInShort: { en: 'Checked in today at 6:42 PM', ar: 'سُجّل دخولك اليوم الساعة 6:42 م' },
  family: { en: 'Family on this membership', ar: 'العائلة على هذه العضوية' },
  you: { en: 'Omar · You', ar: 'عمر · أنت' },
  childOne: { en: 'Yousef · 12', ar: 'يوسف · 12' },
  childTwo: { en: 'Lulwa · 9', ar: 'لولوة · 9' },
  guestButton: { en: 'Send a guest pass', ar: 'أرسل دعوة ضيف' },
  guestTitle: { en: 'Guest pass', ar: 'دعوة ضيف' },
  guestLabel: { en: 'Guest', ar: 'الضيف' },
  guestName: { en: 'Lina Haddad', ar: 'لينا حداد' },
  dateLabel: { en: 'Date', ar: 'التاريخ' },
  dateValue: { en: 'Sat 4 Oct', ar: 'السبت 4 أكتوبر' },
  visitsLabel: { en: 'Valid for', ar: 'الصلاحية' },
  visitsValue: { en: 'One visit', ar: 'زيارة واحدة' },
  send: { en: 'Send pass', ar: 'إرسال الدعوة' },
  guestSent: { en: 'Guest pass sent to Lina · Sat', ar: 'أُرسلت الدعوة إلى لينا · السبت' },
  waApp: { en: 'WhatsApp', ar: 'واتساب' },
  now: { en: 'now', ar: 'الآن' },
  waSender: { en: 'The Harbour Club', ar: 'نادي هاربر' },
  waBody: {
    en: 'Your membership renews on 1 Oct. Renew in one tap.',
    ar: 'تتجدد عضويتك في 1 أكتوبر. جدّدها بلمسة واحدة.',
  },
  waLink: { en: 'Renew membership', ar: 'تجديد العضوية' },
  ...APP_TABS,
} as const;

const CARD_BG = 'linear-gradient(135deg, #2f4a3a 0%, #1c2e24 100%)';

export function MembershipDemo({ locale, className }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS, { staticStep: STEP.guestSent });
  const copy = localize(COPY, locale);
  const brand = DEMO_BRANDS.club;
  const step = timeline.step;

  const scanning = step === STEP.scan;
  const welcome = step === STEP.welcome;
  const checkedIn = step >= STEP.checkedIn;
  const sheetOpen = step === STEP.guestSheet;
  const guestSent = step >= STEP.guestSent;

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <PhoneFrame statusTone={welcome ? 'light' : 'dark'}>
        <div className="absolute inset-0 flex flex-col bg-[#fbfaf8] px-4 pt-[44px]">
          <div className="flex items-center justify-between px-1 pb-3 pt-2">
            <div className="flex items-center gap-2.5">
              <BrandMark brand={brand} size={28} />
              <span className="text-[13px] font-semibold text-[#202020]">
                {brand.name[locale]}
              </span>
            </div>
            <Avatar initials={locale === 'ar' ? 'ع' : 'OR'} tone={4} size={28} />
          </div>

          <h3 className="px-1 text-[21px] font-bold leading-tight text-[#202020]">
            {copy.title}
          </h3>

          <div className="relative mt-3 h-[20px]">
            <Reveal show={scanning} className="absolute inset-0 flex items-center gap-1.5 px-1">
              <ScanLine className="h-3.5 w-3.5" style={{ color: brand.accent }} />
              <span className="text-[10.5px] font-medium text-[#5c554b]">{copy.scanHint}</span>
            </Reveal>
            <Reveal show={checkedIn} className="absolute inset-0 flex items-center gap-1.5 px-1">
              <CheckBadge show={checkedIn} size={14} delay={0.1} />
              <span className="text-[10.5px] font-medium text-[#2f7a63]">{copy.checkedInShort}</span>
            </Reveal>
          </div>

          <motion.div
            className="relative mt-1.5 h-[150px] overflow-hidden rounded-[18px] p-4 text-white"
            style={{ background: CARD_BG }}
            initial={false}
            animate={{
              scale: scanning ? 1.03 : 1,
              y: scanning ? -2 : 0,
              boxShadow: scanning
                ? '0 24px 44px -18px rgba(28,46,36,0.75)'
                : '0 14px 30px -18px rgba(28,46,36,0.6)',
            }}
            transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
          >
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0 2px, transparent 2px 12px)',
              }}
            />
            <span className="absolute -bottom-16 -start-10 h-40 w-40 rounded-full bg-white/[0.06]" />

            <div className="relative flex h-full items-stretch justify-between gap-3">
              <div className="flex min-w-0 flex-col justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex h-6 w-6 items-center justify-center rounded-[7px] bg-white text-[12px] font-bold"
                    style={{ color: brand.accent }}
                  >
                    {brand.mark}
                  </span>
                  <span className="text-[10.5px] font-medium text-white/85">{brand.name[locale]}</span>
                </div>
                <div>
                  <p className="text-[15px] font-semibold leading-tight">{copy.memberName}</p>
                  <p className="mt-0.5 text-[10px] text-white/75">{copy.tier}</p>
                  <p className="mt-2 font-roboto-mono text-[8.5px] uppercase leading-relaxed tracking-[0.1em] text-white/55 rtl:tracking-normal">
                    {copy.since}
                    <br />
                    {copy.validUntil}
                  </p>
                </div>
              </div>

              <div className="relative flex h-[68px] w-[68px] shrink-0 items-center justify-center self-center rounded-[12px] bg-white">
                <FakeQr size={56} seed={23} color="#1c2e24" />
                <motion.span
                  className="absolute inset-x-1.5 h-[2px] rounded-full bg-[#7ee2a8] shadow-[0_0_10px_#7ee2a8]"
                  initial={false}
                  animate={
                    scanning && timeline.isRunning
                      ? { opacity: 1, top: ['12%', '86%', '12%'] }
                      : { opacity: 0, top: '12%' }
                  }
                  transition={
                    scanning && timeline.isRunning
                      ? { duration: 1.2, ease: 'easeInOut', repeat: Infinity }
                      : { duration: 0.2 }
                  }
                />
                <ScanCorners show={scanning} />
              </div>
            </div>
          </motion.div>

          <div className="mt-4">
            <p className="px-1 font-roboto-mono text-[9.5px] uppercase tracking-[0.12em] text-[#8a857c] rtl:tracking-normal">
              {copy.family}
            </p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[
                { initials: locale === 'ar' ? 'ع' : 'OR', label: copy.you, tone: 4 },
                { initials: locale === 'ar' ? 'ي' : 'YR', label: copy.childOne, tone: 0 },
                { initials: locale === 'ar' ? 'ل' : 'LR', label: copy.childTwo, tone: 3 },
              ].map((member) => (
                <div
                  key={member.label}
                  className="flex flex-col items-center gap-1 rounded-[12px] border border-[#ebe7e1] bg-white py-2"
                >
                  <Avatar initials={member.initials} tone={member.tone} size={26} />
                  <span className="whitespace-nowrap text-[9.5px] font-medium text-[#202020]">
                    {member.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <motion.div
              className="relative flex h-10 items-center justify-center gap-2 overflow-hidden rounded-[14px] border bg-white text-[12px] font-semibold"
              style={{ borderColor: brand.accent, color: brand.accent }}
              initial={false}
              animate={{ scale: sheetOpen ? [1, 0.96, 1] : 1 }}
              transition={{ duration: 0.3 }}
            >
              <UserPlus className="h-3.5 w-3.5" />
              {copy.guestButton}
              <TapRipple
                key={`guest-${timeline.cycle}`}
                show={sheetOpen}
                className="left-1/2 top-1/2"
              />
            </motion.div>
          </div>

          <Reveal
            show={guestSent}
            delay={0.35}
            className="mt-2 flex items-center gap-2 rounded-[12px] bg-[#ddf5ec] px-3 py-2"
          >
            <CheckBadge show={guestSent} size={16} delay={0.45} />
            <span className="text-[10.5px] font-medium text-[#2f7a63]">{copy.guestSent}</span>
          </Reveal>

          <AppTabBar
            active={3}
            accent={brand.accent}
            labels={[copy.home, copy.book, copy.bookings, copy.profile]}
          />
        </div>

        {/* Check-in welcome */}
        <motion.div
          className="absolute inset-0 z-40 flex flex-col items-center justify-center px-6 text-center text-white"
          style={{ background: CARD_BG, pointerEvents: 'none' }}
          initial={false}
          animate={{ opacity: welcome ? 1 : 0, scale: welcome ? 1 : 1.04 }}
          transition={{ duration: 0.45, ease: EASE_OUT_EXPO }}
        >
          <CheckBadge show={welcome} size={60} bg="#3f8f63" delay={0.15} />
          <Reveal show={welcome} delay={0.3} className="mt-4">
            <p className="text-[21px] font-bold">{copy.welcome}</p>
            <p className="mt-1.5 text-[11px] text-white/70">{copy.checkedIn}</p>
          </Reveal>
          <Reveal show={welcome} delay={0.45} className="mt-5">
            <span className="rounded-full bg-white/15 px-3 py-1 text-[10.5px] font-medium">
              {copy.tier}
            </span>
          </Reveal>
        </motion.div>

        {/* Guest pass sheet */}
        <motion.div
          className="absolute inset-0 z-10 bg-black/25"
          initial={false}
          animate={{ opacity: sheetOpen ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
        <motion.div
          className="absolute inset-x-0 bottom-0 z-20 rounded-t-[26px] bg-white px-5 pb-8 pt-3"
          initial={false}
          animate={{ y: sheetOpen ? 0 : '105%' }}
          transition={{ duration: 0.55, ease: EASE_OUT_EXPO, delay: sheetOpen ? 0.25 : 0 }}
        >
          <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-[#dcd7cf]" />
          <p className="text-[16px] font-bold text-[#202020]">{copy.guestTitle}</p>
          <div className="mt-3 divide-y divide-[#ebe7e1] rounded-[14px] border border-[#ebe7e1]">
            <SheetRow label={copy.guestLabel} value={copy.guestName} />
            <SheetRow label={copy.dateLabel} value={copy.dateValue} />
            <SheetRow label={copy.visitsLabel} value={copy.visitsValue} />
          </div>
          <motion.div
            className="relative mt-4 flex h-11 items-center justify-center overflow-hidden rounded-[14px] text-[13px] font-semibold text-white"
            style={{ background: brand.accent }}
            initial={false}
            animate={{ scale: sheetOpen ? [1, 1, 0.96, 1] : 1 }}
            transition={{ duration: 1.8, times: [0, 0.8, 0.88, 1] }}
          >
            {copy.send}
          </motion.div>
        </motion.div>

        {/* WhatsApp renewal reminder */}
        <motion.div
          className="absolute inset-x-2.5 top-[46px] z-50 rounded-[20px] bg-white/95 p-3 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.35)] ring-1 ring-black/5 backdrop-blur"
          initial={false}
          animate={{
            y: step === STEP.renewal ? 0 : -150,
            opacity: step === STEP.renewal ? 1 : 0,
          }}
          transition={{ ...SPRING_SNAPPY, delay: step === STEP.renewal ? 0.2 : 0 }}
        >
          <div className="flex items-center gap-2">
            <span
              className="flex h-[18px] w-[18px] items-center justify-center rounded-[5px] text-white"
              style={{ background: DEMO_COLORS.whatsapp }}
            >
              <MessageCircle className="h-3 w-3" strokeWidth={2.5} />
            </span>
            <span className="flex-1 text-[10px] font-medium uppercase tracking-wide text-[#8a857c] rtl:tracking-normal">
              {copy.waApp}
            </span>
            <span className="text-[9.5px] text-[#8a857c]">{copy.now}</span>
          </div>
          <p className="mt-1.5 text-[12.5px] font-semibold text-[#202020]">{copy.waSender}</p>
          <p className="text-[11px] leading-snug text-[#5c554b]">{copy.waBody}</p>
          <span
            className="mt-2 inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold"
            style={{ background: DEMO_COLORS.whatsappBubble, color: '#127a3f' }}
          >
            {copy.waLink}
          </span>
        </motion.div>
      </PhoneFrame>
    </DemoStage>
  );
}

function ScanCorners({ show }: { show: boolean }) {
  const corner = 'absolute h-3 w-3 border-[#7ee2a8]';

  return (
    <motion.span
      className="pointer-events-none absolute -inset-1.5"
      initial={false}
      animate={{ opacity: show ? 1 : 0, scale: show ? 1 : 1.15 }}
      transition={{ duration: 0.35, ease: EASE_OUT_EXPO }}
    >
      <span className={`${corner} left-0 top-0 rounded-tl-[6px] border-l-2 border-t-2`} />
      <span className={`${corner} right-0 top-0 rounded-tr-[6px] border-r-2 border-t-2`} />
      <span className={`${corner} bottom-0 left-0 rounded-bl-[6px] border-b-2 border-l-2`} />
      <span className={`${corner} bottom-0 right-0 rounded-br-[6px] border-b-2 border-r-2`} />
    </motion.span>
  );
}

function SheetRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5">
      <span className="text-[11px] text-[#8a857c]">{label}</span>
      <span className="text-[12px] font-medium text-[#202020]">{value}</span>
    </div>
  );
}
